import React, { useState, useCallback, useRef } from 'react';
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiCode,
  FiList,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiAlignJustify,
  FiImage,
  FiLink,
  FiYoutube,
  FiMinus,
  FiChevronDown,
  FiTable,
  FiType,
  FiEdit3,
  FiTrash2,
  FiPlus,
} from 'react-icons/fi';
import {
  RiH1,
  RiH2,
  RiH3,
  RiH4,
  RiListOrdered,
  RiDoubleQuotesL,
  RiStrikethrough,
  RiCodeBoxLine,
} from 'react-icons/ri';
import './MenuBar.css';

const MenuBar = ({
  editor,
  onImageInsert,
  onYoutubeInsert,
  onLinkInsert,
  onTableInsert,
}) => {
  const [showHeadingMenu, setShowHeadingMenu] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const fileInputRef = useRef(null);

  if (!editor) return null;

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onImageInsert) {
      onImageInsert(file);
    }
    e.target.value = '';
  };

  const handleLinkSubmit = () => {
    if (linkUrl) {
      onLinkInsert?.(linkUrl);
      setLinkUrl('');
      setShowLinkModal(false);
    }
  };

  const handleYoutubeSubmit = () => {
    if (youtubeUrl) {
      onYoutubeInsert?.(youtubeUrl);
      setYoutubeUrl('');
      setShowYoutubeModal(false);
    }
  };

  const handleTableSubmit = () => {
    onTableInsert?.(tableRows, tableCols);
    setShowTableModal(false);
  };

  const MenuButton = ({ onClick, isActive, disabled, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`menu-button ${isActive ? 'active' : ''}`}
      title={title}
    >
      {children}
    </button>
  );

  const MenuDivider = () => <div className="menu-divider" />;

  return (
    <div className="menu-bar">
      <div className="menu-bar-group">
        {/* Heading Dropdown */}
        <div className="menu-dropdown">
          <button
            type="button"
            className="menu-button dropdown-trigger"
            onClick={() => setShowHeadingMenu(!showHeadingMenu)}
          >
            <FiType size={16} />
            <span>Heading</span>
            <FiChevronDown size={14} />
          </button>
          {showHeadingMenu && (
            <div className="dropdown-menu">
              <button
                type="button"
                onClick={() => {
                  editor.chain().focus().setParagraph().run();
                  setShowHeadingMenu(false);
                }}
                className={editor.isActive('paragraph') ? 'active' : ''}
              >
                <span>Paragraph</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level: 1 }).run();
                  setShowHeadingMenu(false);
                }}
                className={editor.isActive('heading', { level: 1 }) ? 'active' : ''}
              >
                <RiH1 size={16} />
                <span>Heading 1</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level: 2 }).run();
                  setShowHeadingMenu(false);
                }}
                className={editor.isActive('heading', { level: 2 }) ? 'active' : ''}
              >
                <RiH2 size={16} />
                <span>Heading 2</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level: 3 }).run();
                  setShowHeadingMenu(false);
                }}
                className={editor.isActive('heading', { level: 3 }) ? 'active' : ''}
              >
                <RiH3 size={16} />
                <span>Heading 3</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level: 4 }).run();
                  setShowHeadingMenu(false);
                }}
                className={editor.isActive('heading', { level: 4 }) ? 'active' : ''}
              >
                <RiH4 size={16} />
                <span>Heading 4</span>
              </button>
            </div>
          )}
        </div>

        <MenuDivider />

        {/* Text Formatting */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <FiBold size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <FiItalic size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <FiUnderline size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Strikethrough"
        >
          <RiStrikethrough size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          isActive={editor.isActive('highlight')}
          title="Highlight"
        >
          <FiEdit3 size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          title="Inline Code"
        >
          <FiCode size={16} />
        </MenuButton>

        <MenuDivider />

        {/* Text Alignment */}
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Align Left"
        >
          <FiAlignLeft size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Align Center"
        >
          <FiAlignCenter size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Align Right"
        >
          <FiAlignRight size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          isActive={editor.isActive({ textAlign: 'justify' })}
          title="Justify"
        >
          <FiAlignJustify size={16} />
        </MenuButton>

        <MenuDivider />

        {/* Lists */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <FiList size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <RiListOrdered size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          isActive={editor.isActive('taskList')}
          title="Task List"
        >
          <span style={{ fontSize: '14px' }}>☐</span>
        </MenuButton>

        <MenuDivider />

        {/* Block Elements */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Quote"
        >
          <RiDoubleQuotesL size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          title="Code Block"
        >
          <RiCodeBoxLine size={16} />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal Rule"
        >
          <FiMinus size={16} />
        </MenuButton>

        <MenuDivider />

        {/* Media */}
        <MenuButton onClick={handleImageClick} title="Insert Image">
          <FiImage size={16} />
        </MenuButton>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          style={{ display: 'none' }}
        />

        <MenuButton
          onClick={() => setShowYoutubeModal(true)}
          title="Insert YouTube Video"
        >
          <FiYoutube size={16} />
        </MenuButton>

        <MenuButton
          onClick={() => setShowLinkModal(true)}
          isActive={editor.isActive('link')}
          title="Insert Link"
        >
          <FiLink size={16} />
        </MenuButton>

        <MenuButton
          onClick={() => setShowTableModal(true)}
          title="Insert Table"
        >
          <FiTable size={16} />
        </MenuButton>
      </div>

      {/* Table Actions (when inside table) */}
      {editor.isActive('table') && (
        <div className="menu-bar-group table-actions">
          <MenuDivider />
          <MenuButton
            onClick={() => editor.chain().focus().addColumnBefore().run()}
            title="Add Column Before"
          >
            <FiPlus size={14} /> Col←
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            title="Add Column After"
          >
            <FiPlus size={14} /> Col→
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().addRowBefore().run()}
            title="Add Row Before"
          >
            <FiPlus size={14} /> Row↑
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().addRowAfter().run()}
            title="Add Row After"
          >
            <FiPlus size={14} /> Row↓
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().deleteColumn().run()}
            title="Delete Column"
          >
            <FiTrash2 size={14} /> Col
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().deleteRow().run()}
            title="Delete Row"
          >
            <FiTrash2 size={14} /> Row
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().deleteTable().run()}
            title="Delete Table"
          >
            <FiTrash2 size={14} /> Table
          </MenuButton>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="menu-modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="menu-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Link</h3>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              autoFocus
            />
            <div className="menu-modal-actions">
              <button type="button" onClick={() => setShowLinkModal(false)}>
                Cancel
              </button>
              <button type="button" onClick={handleLinkSubmit} className="primary">
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* YouTube Modal */}
      {showYoutubeModal && (
        <div className="menu-modal-overlay" onClick={() => setShowYoutubeModal(false)}>
          <div className="menu-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Insert YouTube Video</h3>
            <input
              type="url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              autoFocus
            />
            <div className="menu-modal-actions">
              <button type="button" onClick={() => setShowYoutubeModal(false)}>
                Cancel
              </button>
              <button type="button" onClick={handleYoutubeSubmit} className="primary">
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Modal */}
      {showTableModal && (
        <div className="menu-modal-overlay" onClick={() => setShowTableModal(false)}>
          <div className="menu-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Table</h3>
            <div className="table-size-inputs">
              <div>
                <label>Rows</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={tableRows}
                  onChange={(e) => setTableRows(parseInt(e.target.value) || 3)}
                />
              </div>
              <div>
                <label>Columns</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={tableCols}
                  onChange={(e) => setTableCols(parseInt(e.target.value) || 3)}
                />
              </div>
            </div>
            <div className="menu-modal-actions">
              <button type="button" onClick={() => setShowTableModal(false)}>
                Cancel
              </button>
              <button type="button" onClick={handleTableSubmit} className="primary">
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuBar;
